import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

export type ConfirmOptions = {
  /** 标题 */
  title?: string;
  /** 内容描述 */
  description?: string | React.ReactNode;
  /** 确认按钮文本 */
  confirmText?: string;
  /** 取消按钮文本 */
  cancelText?: string;
  /** 确认按钮样式类型 */
  confirmVariant?: 'default' | 'destructive';
  /** 是否显示取消按钮 */
  showCancel?: boolean;
  /** 是否禁用确认按钮 */
  confirmDisabled?: boolean;
};

export type WaitConfirmOptions = ConfirmOptions & {
  /** 点击确认后的回调，返回 Promise */
  onConfirm: () => Promise<void>;
};

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  resolve?: (value: boolean) => void;
  onConfirm?: () => Promise<void>;
  isLoading?: boolean;
}

export type GlobalConfimFn<O extends ConfirmOptions | WaitConfirmOptions = ConfirmOptions> = (
  options: O
) => Promise<boolean>;

// 全局确认方法引用
export let globalConfirm: GlobalConfimFn | null = null;
export let globalWaitConfirm: GlobalConfimFn<WaitConfirmOptions> | null = null;

/**
 * 全局确认弹窗组件
 * 需要在应用根组件中渲染一次
 */
export function ConfirmDialog() {
  const [state, setState] = useState<ConfirmState>({
    open: false,
    title: '确认',
    description: '',
    confirmText: '确认',
    cancelText: '取消',
    confirmVariant: 'default',
    showCancel: true,
    confirmDisabled: false,
    isLoading: false,
  });

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: options.title ?? '确认',
        description: options.description ?? '',
        confirmText: options.confirmText ?? '确认',
        cancelText: options.cancelText ?? '取消',
        confirmVariant: options.confirmVariant ?? 'default',
        showCancel: options.showCancel ?? true,
        confirmDisabled: options.confirmDisabled ?? false,
        resolve,
        isLoading: false,
        onConfirm: undefined,
      });
    });
  }, []);

  const waitConfirm = useCallback((options: WaitConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: options.title ?? '确认',
        description: options.description ?? '',
        confirmText: options.confirmText ?? '确认',
        cancelText: options.cancelText ?? '取消',
        confirmVariant: options.confirmVariant ?? 'default',
        showCancel: options.showCancel ?? true,
        confirmDisabled: options.confirmDisabled ?? false,
        resolve,
        isLoading: false,
        onConfirm: options.onConfirm,
      });
    });
  }, []);

  // 注册全局方法
  useEffect(() => {
    globalConfirm = confirm;
    globalWaitConfirm = waitConfirm;
    return () => {
      globalConfirm = null;
      globalWaitConfirm = null;
    };
  }, [confirm, waitConfirm]);

  const handleConfirm = async () => {
    if (state.onConfirm) {
      setState((prev) => ({ ...prev, isLoading: true }));
      try {
        await state.onConfirm();
        state.resolve?.(true);
        setState((prev) => ({ ...prev, open: false, isLoading: false }));
      } catch (error) {
        console.error(error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } else {
      state.resolve?.(true);
      setState((prev) => ({ ...prev, open: false }));
    }
  };

  const handleCancel = () => {
    if (state.isLoading) return;
    state.resolve?.(false);
    setState((prev) => ({ ...prev, open: false }));
  };

  const handleOpenChange = (open: boolean) => {
    if (state.isLoading) return;
    if (!open) {
      state.resolve?.(false);
      setState((prev) => ({ ...prev, open: false }));
    }
  };

  return (
    <Dialog open={state.open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{state.title}</DialogTitle>
          <div className="text-muted-foreground text-base">{state.description}</div>
        </DialogHeader>
        <DialogFooter>
          {state.showCancel && (
            <Button variant="outline" onClick={handleCancel} disabled={state.isLoading}>
              {state.cancelText}
            </Button>
          )}
          <Button
            variant={state.confirmVariant === 'destructive' ? 'destructive' : 'default'}
            onClick={handleConfirm}
            disabled={state.isLoading || state.confirmDisabled}
          >
            {state.isLoading && <Spinner className="mr-2 h-4 w-4" />}
            {state.confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
